import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { 
  Plus, 
  PencilSimple, 
  Trash,
  Buildings,
  Clock,
  CurrencyCircleDollar,
  CalendarX
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const AdminCourts = () => {
  const { t, language, API } = useAuth();
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_indoor: true,
    opening_time: '07:00',
    closing_time: '22:00',
    booking_slot_minutes: 90,
    price_per_slot: 60
  });

  useEffect(() => {
    loadCourts();
  }, []);

  const loadCourts = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/courts');
      setCourts(data);
    } catch (error) {
      console.error('Error loading courts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCourt) {
        await API.put(`/courts/${editingCourt.id}`, formData);
        toast.success(language === 'de' ? 'Platz aktualisiert!' : 'Court updated!');
      } else {
        await API.post('/courts', formData);
        toast.success(language === 'de' ? 'Platz erstellt!' : 'Court created!');
      }
      setShowModal(false);
      setEditingCourt(null);
      resetForm();
      loadCourts();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'de' ? 'Fehler' : 'Error');
      toast.error(msg);
    }
  };

  const handleEdit = (court) => {
    setEditingCourt(court);
    setFormData({
      name: court.name,
      description: court.description || '',
      is_indoor: court.is_indoor,
      opening_time: court.opening_time,
      closing_time: court.closing_time,
      booking_slot_minutes: court.booking_slot_minutes,
      price_per_slot: court.price_per_slot
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      is_indoor: true,
      opening_time: '07:00',
      closing_time: '22:00',
      booking_slot_minutes: 90,
      price_per_slot: 60
    });
  };

  const handleBlockDate = async (courtId, date) => {
    try {
      await API.post(`/courts/${courtId}/block`, { date });
      toast.success(language === 'de' ? 'Datum blockiert' : 'Date blocked');
      loadCourts();
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler' : 'Error');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-courts">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            {t('courts')}
          </h1>
          <p className="text-slate-500">
            {language === 'de' 
              ? 'Verwalten Sie Ihre Padel-Plätze'
              : 'Manage your padel courts'}
          </p>
        </div>
        
        <Button 
          onClick={() => { resetForm(); setEditingCourt(null); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-court-btn"
        >
          <Plus size={18} weight="bold" />
          {language === 'de' ? 'Platz hinzufügen' : 'Add Court'}
        </Button>
      </div>

      {/* Courts Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : courts.length === 0 ? (
        <div className="card-static p-12 text-center">
          <Buildings size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
          <p className="text-slate-500 mb-4">
            {language === 'de' ? 'Keine Plätze vorhanden' : 'No courts yet'}
          </p>
          <Button onClick={() => setShowModal(true)} className="btn-primary">
            {language === 'de' ? 'Ersten Platz erstellen' : 'Create First Court'}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courts.map((court, index) => (
            <motion.div
              key={court.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card-static p-6"
              data-testid={`court-card-${court.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">{court.name}</h3>
                  <p className="text-slate-500 text-sm">{court.description}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-bold uppercase rounded-lg ${
                  court.is_indoor 
                    ? 'bg-blue-50 text-blue-600' 
                    : 'bg-green-50 text-green-600'
                }`}>
                  {court.is_indoor ? 'Indoor' : 'Outdoor'}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-slate-500">
                  <Clock size={16} weight="duotone" />
                  <span>{court.opening_time} - {court.closing_time}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-500">
                  <CurrencyCircleDollar size={16} weight="duotone" />
                  <span>{court.price_per_slot} CHF / {court.booking_slot_minutes} min</span>
                </div>
                {court.blocked_dates?.length > 0 && (
                  <div className="flex items-center gap-3 text-amber-600">
                    <CalendarX size={16} weight="duotone" />
                    <span>{court.blocked_dates.length} {language === 'de' ? 'blockierte Tage' : 'blocked days'}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => handleEdit(court)}
                  variant="outline"
                  className="flex-1 btn-secondary text-sm"
                  data-testid={`edit-court-${court.id}`}
                >
                  <PencilSimple size={16} className="mr-2" />
                  {t('edit')}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="court-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingCourt 
                ? (language === 'de' ? 'Platz bearbeiten' : 'Edit Court')
                : (language === 'de' ? 'Neuer Platz' : 'New Court')}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {language === 'de' 
                ? 'Füllen Sie die Platzdetails aus'
                : 'Fill in the court details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Name' : 'Name'} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-slate-50 border-slate-200 text-slate-900"
                placeholder="Court 1"
                data-testid="court-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Beschreibung' : 'Description'}</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-50 border-slate-200 text-slate-900"
                placeholder={language === 'de' ? 'z.B. Panorama-Aussicht' : 'e.g. Panorama view'}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <Label className="label-text">Indoor</Label>
              <Switch 
                checked={formData.is_indoor}
                onCheckedChange={(checked) => setFormData({...formData, is_indoor: checked})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Öffnung' : 'Opening'}</Label>
                <Input
                  type="time"
                  value={formData.opening_time}
                  onChange={(e) => setFormData({...formData, opening_time: e.target.value})}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Schliessung' : 'Closing'}</Label>
                <Input
                  type="time"
                  value={formData.closing_time}
                  onChange={(e) => setFormData({...formData, closing_time: e.target.value})}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Slot (min)' : 'Slot (min)'}</Label>
                <Input
                  type="number"
                  value={formData.booking_slot_minutes}
                  onChange={(e) => setFormData({...formData, booking_slot_minutes: parseInt(e.target.value)})}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Preis (CHF)' : 'Price (CHF)'}</Label>
                <Input
                  type="number"
                  value={formData.price_per_slot}
                  onChange={(e) => setFormData({...formData, price_per_slot: parseFloat(e.target.value)})}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowModal(false)}
                className="flex-1 btn-secondary"
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!formData.name}
                className="flex-1 btn-primary"
                data-testid="submit-court"
              >
                {t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCourts;
